import React, { useState, useMemo, useCallback } from 'react';
import { TabState, DataValue, DatasetId, createColumnName, createDatasetId, EphemeralDataSet, FilterExpression } from '../../../../types';
import { MeasurementScale, Scale, HomogeneousArray } from '../../../../types/scale.types';
import { DerivedDataSet, SQLQueryOperation, PrimaryDataSet } from '../../../../types/dataset.types';
import { getColumnDataSafe } from '../../../utils/column-utils';
import { SelectableDataTable } from '../../SelectableDataTable';
import { DatasetReloadModal } from '../../DatasetReloadModal';
import { DatasetMetadataModal } from '../../DatasetMetadataModal';
import { Dialog } from '../../common/Dialog';
import { Button } from '../../common/Button';
import { TextField } from '../../common/TextField';
import { useProjectData } from "../../../stores/hooks/useProjectData"
import { useDatasetSelection } from "../../../stores/hooks/useDatasetSelection"
import { useDatasetWithData } from "../../../hooks/useDatasetWithData"
import { useTabOperations } from "../../../hooks/useTabOperations"
import { usePaneLayout } from "../../../stores/hooks/usePaneLayout"
import { useProjectStore } from '../../../stores/projectStore';
import { useNotificationStore } from '../../../stores/notificationStore';
import { createDataTableTab, createSQLQueryViewerTab, createColumnTypeConversionTab } from '../../../tab-system/TabRegistry';
import { FilterExpressionParser } from '../../../utils/FilterExpressionParser';
import { convertEphemeralToDerivedDataset } from '../../../utils/convertEphemeralToDerivedDataset';
import logger from '../../../utils/logger';
import * as styles from '../../../tab-system/TabContainer.css.ts';

/**
 * DataTableTabContent
 * 
 * リファクタリング後のDataTableタブコンテンツ
 * - Props Drillingを排除し、Contextから直接データを取得
 * - 自己完結したコンポーネント
 */

interface DataTableTabContentProps {
  tab: TabState;
}

export const DataTableTabContent: React.FC<DataTableTabContentProps> = ({ tab }) => {
  const { project, getDataset, updateDataset, updateProject } = useProjectData();
  const paneLayout = usePaneLayout();
  const tabOperations = useTabOperations({
    project,
    layout: {
      rootPane: paneLayout.layout.rootPane,
      activeContainerId: paneLayout.layout.activeContainerId ?? null
    },
    addTab: paneLayout.addTab,
    addTabWithOptions: paneLayout.addTabWithOptions,
    closeTab: paneLayout.closeTab,
    moveTabWithinPane: paneLayout.moveTabWithinPane,
    moveTabToPane: paneLayout.moveTabToPane,
    startDrag: paneLayout.startDrag,
    endDrag: paneLayout.endDrag
  });
  const [filterExpression, setFilterExpression] = useState('');
  const [filterError, setFilterError] = useState<string | null>(null);
  const [showReloadModal, setShowReloadModal] = useState(false);
  const [showMetadataModal, setShowMetadataModal] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [datasetName, setDatasetName] = useState('');
  const { error: showError } = useNotificationStore();
  // ソート条件をローカルステートとして管理（DOM永続化により保持される）
  const [sortConditions, setSortConditions] = useState<Array<{ column: string; direction: 'asc' | 'desc' }>>([]);
  
  // タブのメタデータからデータセットIDを取得
  const datasetId = tab.metadata?.datasetId;
  
  const dataset = datasetId ? getDataset(datasetId) : undefined;
  
  // SQLクエリ操作があるかチェック（DerivedDataSetかつsql_query typeの場合）
  const sqlOperation = useMemo(() => {
    if (dataset?.type === 'derived') {
      const derivedDataset = dataset as DerivedDataSet;
      if (derivedDataset.operation?.type === 'sql_query') {
        return derivedDataset.operation as SQLQueryOperation;
      }
    }
    return null;
  }, [dataset]);

  // SQLクエリを別タブで表示
  const handleViewSQLQuery = useCallback(() => {
    if (!dataset || !datasetId || !sqlOperation) return;
    
    const activeContainerId = tabOperations.getActiveContainerId();
    if (!activeContainerId) return;
    
    // SQL Query Viewerタブを作成
    const newTab = createSQLQueryViewerTab(
      datasetId,
      `SQL: ${dataset.name}`
    );

    // タブを追加
    paneLayout.addTab(activeContainerId, newTab);
    logger.info('[DataTableTabContent] Opened SQL query viewer for:', dataset.name);
  }, [dataset, datasetId, sqlOperation, tabOperations, paneLayout]);

  // データセットリロード処理
  const handleReloadDataset = useCallback(async (source: string | File) => {
    if (!project || !datasetId || !dataset || dataset.type !== 'primary') {
      logger.warn('[DataTableTabContent] Cannot reload dataset:', {
        hasProject: !!project,
        datasetId,
        datasetType: dataset?.type
      });
      throw new Error('Cannot reload dataset');
    }

    // Web版の場合
    try {
      const { webReloadService } = await import('../../../services/webReloadService');

      // リロード実行（sourceはFileまたはURL文字列）
      const reloadedDataset = await webReloadService.reloadDataset(
        dataset as PrimaryDataSet,
        source
      );

      // DuckDBの全テーブルをクリア（PrimaryDataSetのデータが変更されたため）
      const { duckdbProxy } = await import('../../../utils/duckdb');
      await duckdbProxy.clearAllTables();

      // プロジェクトストアを更新
      updateProject((currentProject) => {
        // 派生データセットのキャッシュをクリア
        const updatedDatasets = { ...currentProject.datasets };
        updatedDatasets[datasetId] = reloadedDataset;

        // このデータセットに推移的に依存する派生データセットを見つけてキャッシュをクリア
        // Issue #257: 直接依存だけでなく、推移的依存も含めてキャッシュをクリアする必要がある
        const collectTransitiveDependents = (targetId: DatasetId): Set<DatasetId> => {
          const dependents = new Set<DatasetId>();
          const queue = [targetId];

          while (queue.length > 0) {
            const currentId = queue.shift()!;

            Object.keys(updatedDatasets).forEach(id => {
              const dsId = id as DatasetId;
              if (dependents.has(dsId)) return; // 既に処理済み

              const ds = updatedDatasets[dsId];
              if (ds.type !== 'derived') return;

              const derivedDs = ds as DerivedDataSet;
              let isDependentOnCurrent = false;

              // parentDatasetIdsにcurrentIdが含まれている場合
              if (derivedDs.parentDatasetIds.includes(currentId)) {
                isDependentOnCurrent = true;
                logger.debug(`[DataTableTabContent] Dataset ${derivedDs.name} depends on ${currentId} via parentDatasetIds`);
              }

              // SQL操作のtargetDatasetIdが一致する場合
              if (derivedDs.operation?.type === 'sql_query') {
                const sqlOp = derivedDs.operation as SQLQueryOperation;
                if (sqlOp.targetDatasetId === currentId) {
                  isDependentOnCurrent = true;
                  logger.debug(`[DataTableTabContent] Dataset ${derivedDs.name} depends on ${currentId} via SQL targetDatasetId`);
                }
              }

              if (isDependentOnCurrent) {
                dependents.add(dsId);
                queue.push(dsId); // 推移的依存を探索するためにキューに追加
              }
            });
          }

          return dependents;
        };

        const dependentIds = collectTransitiveDependents(datasetId);
        dependentIds.forEach(dsId => {
          const derivedDs = updatedDatasets[dsId] as DerivedDataSet;
          logger.info(`[DataTableTabContent] Clearing cache for derived dataset: ${derivedDs.name}`);
          updatedDatasets[dsId] = {
            ...derivedDs,
            data: undefined // キャッシュをクリア
          };
        });

        return {
          ...currentProject,
          datasets: updatedDatasets
        };
      });

      logger.info('[DataTableTabContent] Dataset reloaded successfully (Web)');
      setShowReloadModal(false);
    } catch (error) {
      logger.error('[DataTableTabContent] Failed to reload dataset:', error);
      throw error;
    }
  }, [project, datasetId, dataset, updateProject]);
  
  // データセットのデータを自動的にロード
  const { isLoading, isDataAvailable, error } = useDatasetWithData(dataset);
  
  // DataTableSelectionContextから選択状態を取得
  const {
    selectedColumns,
    selectedRows,
    selectColumns,
    selectRows,
    resetSelection
  } = useDatasetSelection(datasetId);
  
  // デバッグログ

  // 列選択ハンドラー（ヒント削除済み）
  const handleColumnSelect = useCallback((columnNames: string[]) => {
    selectColumns(columnNames);
  }, [selectColumns]);
  
  // フィルタ式をパースする（純粋関数として）
  const parseResult = useMemo(() => {
    if (!filterExpression) {
      return null;
    }
    
    const parser = new FilterExpressionParser();
    return parser.parse(filterExpression);
  }, [filterExpression]);
  
  // パース結果に基づいてエラー状態を更新
  React.useEffect(() => {
    if (!filterExpression) {
      setFilterError(null);
    } else if (parseResult && !parseResult.success) {
      setFilterError(parseResult.error || 'Failed to parse filter expression');
    } else {
      setFilterError(null);
    }
  }, [filterExpression, parseResult]);
  
  // フィルタされた行インデックスを計算（純粋関数として）
  const filteredRowIndices = useMemo(() => {
    if (!filterExpression || !dataset?.data || !parseResult?.success) {
      return null;
    }
    
    const expression = parseResult.expression!;
    
    // データの各行に対してフィルタを適用
    const indices: number[] = [];
    const columns = dataset.schema?.columns || [];
    const firstColumnData = columns.length > 0 ? getColumnDataSafe(dataset, columns[0].id) : null;
    const rowCount = firstColumnData?.length || 0;
    
    for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
      // 行データをカラム名でアクセスできるオブジェクトに変換
      const rowData: Record<string, DataValue> = {};
      columns.forEach((col) => {
        const columnData = getColumnDataSafe(dataset, col.id);
        rowData[col.name] = columnData?.[rowIndex] ?? null;
      });
      
      if (FilterExpressionParser.evaluate(expression, rowData)) {
        indices.push(rowIndex);
      }
    }
    
    return indices.length > 0 ? indices : [];
  }, [filterExpression, dataset, parseResult]);
  
  // カラム名変更ハンドラー
  const handleColumnRename = (columnId: string, newName: string) => {
    if (!datasetId || !dataset) return;

    updateDataset(datasetId, dataset => {
      // スキーマのカラムを更新
      if (!dataset.schema?.columns) return dataset;

      return {
        ...dataset,
        schema: {
          ...dataset.schema,
          columns: dataset.schema.columns.map(col =>
            col.id === columnId ? { ...col, name: createColumnName(newName) } : col
          )
        }
      };
    });
  };
  
  // 尺度変更ハンドラー
  const handleScaleChange = (
    columnId: string, 
    scale: MeasurementScale,
    ordinalOrder?: HomogeneousArray,
    validCategories?: HomogeneousArray
  ) => {
    if (!datasetId || !dataset) return;
    
    logger.info('[DataTableTabContent] Changing scale for column', {
      datasetId,
      columnId,
      newScale: scale,
      hasOrdinalOrder: !!ordinalOrder,
      hasValidCategories: !!validCategories
    });
    
    updateDataset(datasetId, dataset => {
      // スキーマのカラムを更新
      if (!dataset.schema?.columns) return dataset;
      
      return {
        ...dataset,
        schema: {
          ...dataset.schema,
          columns: dataset.schema.columns.map(col => {
            if (col.id === columnId) {
              // ユーザー指定の尺度として更新
              const newScale: Scale = {
                scale,
                inferredAutomatically: false,
                inferenceReason: 'User specified'
              };
              
              // 尺度タイプに応じてプロパティを設定
              if (scale === 'nominal' && validCategories) {
                newScale.validCategories = validCategories;
              }
              
              if (scale === 'ordinal') {
                if (ordinalOrder) {
                  newScale.ordinalOrder = ordinalOrder;
                }
                if (validCategories) {
                  newScale.validCategories = validCategories;
                }
              }
              
              return { ...col, scale: newScale };
            }
            return col;
          })
        }
      };
    });
  };

  // 列型変換タブを開く
  const handleOpenColumnTypeConversion = useCallback((columnId: string) => {
    if (!datasetId) return;

    const activeContainerId = tabOperations.getActiveContainerId();
    if (!activeContainerId) return;

    // 列型変換タブを作成
    const newTab = createColumnTypeConversionTab(datasetId, columnId);

    // タブを追加
    paneLayout.addTab(activeContainerId, newTab);
    logger.info('[DataTableTabContent] Opened column type conversion tab for column:', columnId);
  }, [datasetId, tabOperations, paneLayout]);

  // データセットが無い場合のプレースホルダー
  if (!dataset) {
    return (
      <div data-testid="data-table-content" className={styles.tabContentScrollable}>
        <div className={styles.dataTableTabContent}>
          <div style={{ padding: '20px', color: '#721c24', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>
            <h3>Dataset not found</h3>
            <p>Dataset ID: <code>{datasetId || 'unknown'}</code></p>
            <p>This may happen if:</p>
            <ul>
              <li>The dataset was deleted</li>
              <li>The dataset ID is incorrect</li>
              <li>The project data is not fully loaded</li>
            </ul>
            <p>Please check the console for more details.</p>
          </div>
        </div>
      </div>
    );
  }
  
  // データのロード中またはエラー時の表示
  if (isLoading) {
    return (
      <div data-testid="data-table-content" className={styles.tabContentScrollable}>
        <div className={styles.dataTableTabContent}>
          <p>Loading dataset "{dataset.name}"...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div data-testid="data-table-content" className={styles.tabContentScrollable}>
        <div className={styles.dataTableTabContent}>
          <p>Error loading dataset "{dataset.name}": {error.message}</p>
        </div>
      </div>
    );
  }
  
  if (!isDataAvailable) {
    return (
      <div data-testid="data-table-content" className={styles.tabContentScrollable}>
        <div className={styles.dataTableTabContent}>
          <p>Dataset "{dataset.name}" has no data available.</p>
        </div>
      </div>
    );
  }

  // フィルタが適用されているかどうか
  const hasActiveFilter = filterExpression.trim() !== '' && filteredRowIndices !== null && parseResult?.success;

  // フィルタ結果をデータセットとして保存
  const handleSaveAsDataset = () => {
    if (!hasActiveFilter || !dataset) return;
    // デフォルト名を設定
    setDatasetName(`${dataset.name} (filtered)`);
    setShowSaveDialog(true);
  };

  // 保存実行
  const handleSave = () => {
    if (!hasActiveFilter || !dataset || !datasetName.trim() || !datasetId || !parseResult?.expression) return;

    try {
      const columns = dataset.schema?.columns || [];

      // フィルタ済みデータを構築
      const filteredData: Record<string, DataValue[]> = {};
      columns.forEach(col => {
        const columnData = getColumnDataSafe(dataset, col.id);
        if (columnData && filteredRowIndices) {
          filteredData[col.id] = filteredRowIndices.map(i => columnData[i]);
        }
      });

      // EphemeralDataSetを作成
      const ephemeralDataset: EphemeralDataSet = {
        id: createDatasetId(`dataset-ephemeral-${crypto.randomUUID()}`),
        name: datasetName.trim(),
        type: 'ephemeral',
        schema: dataset.schema,
        data: filteredData,
        rowCount: filteredRowIndices?.length || 0,
        metadata: {
          sourceDatasetId: datasetId,
          filterExpression: parseResult.expression as FilterExpression,
          description: `Filtered: ${filterExpression}`,
          createdAt: new Date().toISOString(),
          purpose: 'filtered_view'
        }
      };

      // DerivedDataSetに変換
      const derivedDataset = convertEphemeralToDerivedDataset(
        ephemeralDataset,
        datasetName.trim()
      );

      // プロジェクトに追加
      useProjectStore.getState().addDataset(derivedDataset);

      logger.info('[DataTableTabContent] Saved filtered data as dataset', {
        name: datasetName.trim(),
        rowCount: filteredRowIndices?.length,
        filterExpression
      });

      // 新しいデータセットのタブを開く
      const newTab = createDataTableTab(derivedDataset.id, derivedDataset.name);
      const activeContainerId = tabOperations.getActiveContainerId();
      if (activeContainerId) {
        paneLayout.addTab(activeContainerId, newTab);
        logger.info('[DataTableTabContent] Opened new dataset tab:', derivedDataset.name);
      } else {
        logger.warn('[DataTableTabContent] No active container found, cannot open tab');
      }

      // ダイアログを閉じる
      setShowSaveDialog(false);
      setDatasetName('');
    } catch (error) {
      logger.error('[DataTableTabContent] Failed to save filtered dataset:', error);
      showError(`Failed to save dataset: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  // 選択をクリアするハンドラー
  const handleClearSelection = () => {
    resetSelection();
    logger.info('[DataTableTabContent] Selection cleared', { datasetId });
  };

  // 選択状態があるかチェック
  const hasSelection = selectedColumns.length > 0 || selectedRows.length > 0;

  return (
    <div data-testid="data-table-content" className={styles.tabContentScrollable}>
      <div className={styles.dataTableTabContent}>
        {/* データセットヘッダー */}
        <div className={styles.datasetHeader}>
          <span className={styles.datasetHeaderTitle}>{dataset.name}</span>
          <button
            className={styles.datasetHeaderInfoButton}
            onClick={() => setShowMetadataModal(true)}
            title="View dataset metadata"
            aria-label="View dataset metadata"
            data-testid="dataset-metadata-button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M8 7V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              <circle cx="8" cy="5" r="0.75" fill="currentColor"/>
            </svg>
          </button>
        </div>

        <div className={styles.filterContainer}>
          <input
            type="text"
            className={styles.filterInput}
            placeholder="Enter filter expression (e.g., age > 25 or name LIKE '%John%')"
            value={filterExpression}
            onChange={(e) => setFilterExpression(e.target.value)}
          />
          {hasSelection && (
            <button
              className={styles.clearSelectionButton}
              onClick={handleClearSelection}
              title="Clear all selections"
              aria-label={`Clear selection: ${selectedColumns.length} columns, ${selectedRows.length} rows`}
            >
              Clear ({selectedColumns.length}c, {selectedRows.length}r) ×
            </button>
          )}
          {hasActiveFilter && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveAsDataset}
              data-testid="save-filtered-data-button"
            >
              Save Filtered Data ({filteredRowIndices?.length} rows)
            </Button>
          )}
        </div>
        {filterError && (
          <div className={styles.filterError}>{filterError}</div>
        )}
        <SelectableDataTable
          dataset={dataset}
          filteredRowIndices={filteredRowIndices}
          selectedColumns={selectedColumns}
          selectedRows={selectedRows}
          onColumnSelect={handleColumnSelect}
          onRowSelect={selectRows}
          onSortChange={setSortConditions}
          sortConditions={sortConditions}
          onColumnRename={handleColumnRename}
          onScaleChange={handleScaleChange}
          onOpenColumnTypeConversion={handleOpenColumnTypeConversion}
          onViewSQLQuery={sqlOperation ? handleViewSQLQuery : undefined}
          onReloadDataset={() => setShowReloadModal(true)}
          showSQLOption={!!sqlOperation}
          showReloadOption={dataset?.type === 'primary'}
        />
      </div>

      {/* データセットリロードモーダル */}
      {showReloadModal && (
        <DatasetReloadModal
          datasetName={dataset?.name || ''}
          originalFileName={dataset?.type === 'primary' ? (dataset as PrimaryDataSet).metadata?.originalFileName : undefined}
          sourceUrl={dataset?.type === 'primary' ? (dataset as PrimaryDataSet).metadata?.sourceUrl : undefined}
          onClose={() => setShowReloadModal(false)}
          onReload={handleReloadDataset}
        />
      )}

      {/* データセットメタデータモーダル */}
      {showMetadataModal && (
        <DatasetMetadataModal
          dataset={dataset}
          onClose={() => setShowMetadataModal(false)}
        />
      )}

      {/* フィルタ結果保存ダイアログ */}
      <Dialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        title="Save Filtered Data as Dataset"
        width="md"
        data-testid="save-filtered-dialog"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowSaveDialog(false)}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!datasetName.trim()}
              data-testid="save-button"
            >
              Save
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <TextField
            label="Dataset Name"
            value={datasetName}
            onChange={(e) => setDatasetName(e.target.value)}
            placeholder="Enter dataset name"
            autoFocus
            fullWidth
            data-testid="dataset-name-input"
          />

          {/* フィルタ情報の表示 */}
          <div style={{
            padding: '12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#1976d2'
          }}>
            <strong>Filter applied:</strong>
            <div style={{ marginTop: '4px', fontFamily: 'monospace' }}>{filterExpression}</div>
            <div style={{ marginTop: '8px', fontSize: '13px' }}>
              Saving {filteredRowIndices?.length} rows (filtered from {dataset.rowCount} rows)
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};